import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../api/auth.ts';
import { billingApi } from '../api/billing.ts';
import { useAuthStore } from '../state/authStore.ts';
import { useSeo } from '../hooks/useSeo.ts';
import { trackPurchase } from '../analytics.ts';

const POLL_INTERVAL_MS = 2_000;
const MAX_POLL_ATTEMPTS = 10; // ~20 seconds total

/**
 * Verification cascade after Stripe redirects here:
 *   1. Call /api/billing/session/{id} → returns isPaid + creditsGranted + planCode.
 *      creditsGranted is authoritative: the backend records the session id on the
 *      credit_transaction row when the webhook fires.
 *   2. Poll /api/auth/me alongside so the store reflects the new balance once it lands.
 *   3. If Stripe says isPaid=false after ~20s: payment is still processing on Stripe's side.
 *   4. If we can't reach Stripe at all: contact-support fallback with session_id in mailto.
 */
export default function BillingSuccessPage() {
  useSeo({
    title: 'Payment confirmation',
    noindex: true,
  });

  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const setUser = useAuthStore(s => s.setUser);

  const [phase, setPhase] = useState<'verifying' | 'paid_no_credits' | 'confirmed' | 'failed' | 'timeout'>('verifying');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const purchaseReported = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase('timeout');
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const [session, me] = await Promise.all([
          billingApi.retrieveSession(sessionId),
          authApi.me(),
        ]);
        if (cancelled) return;

        if (me) setUser(me);

        if (session.creditsGranted) {
          setCreditsAdded(session.creditsAmount > 0 ? session.creditsAmount : 1);
          setPhase('confirmed');

          // Client-side purchase event. The server already fired the authoritative GA4 MP
          // event from the Stripe webhook; this client-side mirror gives Google Ads a same-session
          // signal (better landing-page conversion attribution) and de-dupes against the server
          // event by transaction_id (= session_id).
          if (!purchaseReported.current && session.planCode) {
            purchaseReported.current = true;
            const planPrice = planCodeToEur(session.planCode);
            if (planPrice !== null) {
              trackPurchase({
                transactionId: session.sessionId,
                value: planPrice,
                currency: 'EUR',
                planCode: session.planCode,
              });
            }
          }
          return;
        }

        if (session.paymentStatus === 'unpaid' && !session.isPaid) {
          setPhase('failed');
          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPhase(session.isPaid ? 'paid_no_credits' : 'timeout');
          return;
        }
      } catch {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPhase('timeout');
          return;
        }
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, setUser]);

  if (phase === 'verifying') {
    return (
      <div className="page success-page">
        <div className="spinner" aria-hidden="true" />
        <h1>Verifying your payment…</h1>
        <p className="muted">Confirming with Stripe — usually under 5 seconds.</p>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="page success-page">
        <h1>Payment couldn't be completed</h1>
        <p>Stripe reports this payment as unpaid. If you saw a card decline or 3-D Secure failure, please try again.</p>
        <div className="success-actions">
          <Link to="/pricing" className="cta">Try again</Link>
          <Link to="/profile" className="cta secondary">Profile</Link>
        </div>
      </div>
    );
  }

  if (phase === 'paid_no_credits') {
    return (
      <div className="page success-page">
        <h1>Payment received — credits delayed</h1>
        <p>
          Stripe confirmed your payment but the credits haven't landed in our system yet.
          Don't pay again — we usually catch up within a minute.
        </p>
        <p className="muted">
          If it's been more than 5 minutes, email{' '}
          <a href={`mailto:support@eaaproof.com?subject=Missing%20credits&body=Session%20ID%3A%20${encodeURIComponent(sessionId ?? '(none)')}`}>
            support@eaaproof.com
          </a>{' '}with your session ID below.
        </p>
        <p className="muted small">Session ID: <code>{sessionId}</code></p>
        <Link to="/profile" className="cta">View profile</Link>
      </div>
    );
  }

  if (phase === 'timeout') {
    return (
      <div className="page success-page">
        <h1>We can't verify this purchase</h1>
        <p>
          We weren't able to reach Stripe to confirm your purchase. This is rare — usually a
          temporary network blip. Don't pay again.
        </p>
        <p className="muted">
          Email{' '}
          <a href={`mailto:support@eaaproof.com?subject=Stripe%20verification%20failed&body=Session%20ID%3A%20${encodeURIComponent(sessionId ?? '(none)')}`}>
            support@eaaproof.com
          </a>{' '}with your session ID and we'll resolve it within an hour.
        </p>
        <p className="muted small">Session ID: <code>{sessionId ?? '(not provided)'}</code></p>
        <Link to="/profile" className="cta">View profile</Link>
      </div>
    );
  }

  function planCodeToEur(code: string): number | null {
    switch (code) {
      case 'pkg_single_scan': return 29;
      case 'sub_basic': return 9;
      case 'sub_agency': return 49;
      default: return null;
    }
  }

  return (
    <div className="page success-page">
      <h1>Thanks for your purchase</h1>
      <p>
        <strong>{creditsAdded} credit{creditsAdded === 1 ? '' : 's'}</strong> added to your account.
      </p>
      <div className="success-actions">
        <Link to="/" className="cta">Run a scan</Link>
        <Link to="/profile" className="cta secondary">View purchase history</Link>
      </div>
    </div>
  );
}
